import { gsap } from 'gsap'
import { Draggable } from 'gsap/Draggable'
import { Flip } from 'gsap/Flip'
import { throttle } from 'lodash-es'

gsap.registerPlugin(Draggable, Flip)

const setupTimeline = (el) => {
  const timeline = el.querySelector('[data-timeline]')
  const blocks = el.querySelectorAll('[data-block]')
  const pattern = el.querySelector('[data-pattern]')
  const back = el.querySelector('[data-back]')
  const forward = el.querySelector('[data-forward]')
  const boxes = el.querySelectorAll('[data-expandable]')
  const overlays = el.querySelectorAll('[data-overlay]')
  const length = blocks.length
  const proxy = document.createElement('div')
  const tl = gsap.timeline({ paused: true })
  const tlSteps = 1 / (length - 1)
  const tlDuration = 10
  const tlStart = Array.from(blocks).findIndex((b) => {
    return b.hasAttribute('data-start')
  })

  proxy.style.pointerEvents = 'none'

  let timelineWidth = null
  let inTransition = false

  function createTimeline() {
    tl.to(pattern, {
      xPercent: `-${(25 / length) * (length - 1)}`,
      duration: tlDuration,
      ease: 'none',
    }).to(
      timeline,
      {
        xPercent: `-${(100 / length) * (length - 1)}`,
        duration: tlDuration,
        ease: 'none',
      },
      0,
    )
  }

  function setupDraggable() {
    Draggable.create(proxy, {
      type: 'x',
      trigger: timeline,
      bounds: { minX: timelineWidth * -1, maxX: 0 },
      onDrag: function () {
        tl.progress((1 - this.x) / timelineWidth)

        gsap.to(blocks, {
          scale: 0.85,
        })
      },
      onDragEnd: function () {
        const time = tlDuration * Math.round(tl.progress() / tlSteps) * tlSteps

        tl.tweenTo(time, {
          ease: 'expo.inOut',
          duration: 0.2,
        })

        gsap.to(blocks, {
          scale: 1,
        })
      },
      onPress: function () {
        gsap.set(this.target, {
          x: tl.progress() * timelineWidth * -1,
        })

        this.update()
      },
    })
  }

  function goForward() {
    if (inTransition === false) {
      tl.tweenTo(tlDuration * (tl.progress() + tlSteps), {
        duration: 0.4,
        ease: 'expo.inOut',
        onStart: () => {
          inTransition = true
        },
        onComplete: () => {
          inTransition = false
        },
      })
    }
  }

  function goBack() {
    if (inTransition === false) {
      tl.tweenTo(tlDuration * (tl.progress() - tlSteps), {
        duration: 0.4,
        ease: 'expo.inOut',
        onStart: () => {
          inTransition = true
        },
        onComplete: () => {
          inTransition = false
        },
      })
    }
  }

  function expandBox(box) {
    const overlay = Array.from(overlays).filter((o) => {
      return o.getAttribute('data-id') === box.getAttribute('data-id')
    })[0]

    const icon = overlay.querySelector('[data-icon]')

    box.classList.add('is-active')
    overlay.classList.add('is-active')

    const boxRect = box.getBoundingClientRect()
    const overlayRect = overlay.getBoundingClientRect()

    gsap.set(overlay, {
      height: boxRect.height,
      width: boxRect.width,
      top: `${boxRect.y - overlayRect.y}`,
    })

    const state = Flip.getState(overlay)

    gsap.set(overlay, {
      clearProps: 'all',
    })

    Flip.from(state, {
      duration: 0.6,
      ease: 'expo.inOut',
      onStart: () => {
        gsap.to(icon, {
          rotate: '45deg',
          duration: 0.6,
          ease: 'expo.inOut',
        })
      },
    })
  }

  function closeBox(overlay) {
    const box = Array.from(boxes).filter((b) => {
      return b.getAttribute('data-id') === overlay.getAttribute('data-id')
    })[0]

    const icon = overlay.querySelector('[data-icon]')

    const state = Flip.getState(overlay)

    const boxRect = box.getBoundingClientRect()
    const overlayRect = overlay.getBoundingClientRect()

    gsap.set(overlay, {
      height: boxRect.height,
      width: boxRect.width,
      top: `${boxRect.y - overlayRect.y}`,
    })

    Flip.from(state, {
      duration: 0.4,
      ease: 'expo.inOut',
      onStart: () => {
        gsap.to(icon, {
          rotate: '0deg',
          duration: 0.4,
          ease: 'expo.inOut',
        })
      },
      onComplete: function () {
        gsap.set(overlay, {
          clearProps: 'all',
        })

        box.classList.remove('is-active')
        overlay.classList.remove('is-active')
      },
    })
  }

  function onResize() {
    window.requestAnimationFrame(() => {
      timelineWidth = timeline.getBoundingClientRect().width
    })
  }

  function init() {
    window.requestAnimationFrame(() => {
      timelineWidth = timeline.getBoundingClientRect().width

      window.requestAnimationFrame(() => {
        gsap.set(timeline, { xPercent: 0 })
        createTimeline()
        setupDraggable()

        if (tlStart > 0) {
          tl.progress(tl.progress() + tlSteps * tlStart)
        }

        boxes.forEach((el) => {
          el.addEventListener('click', expandBox.bind(null, el), false)
        })

        overlays.forEach((el) => {
          el.addEventListener('click', closeBox.bind(null, el), false)
        })

        back.addEventListener('click', goBack, false)
        forward.addEventListener('click', goForward, false)
        window.addEventListener('resize', throttle(onResize, 100), false)
      })
    })
  }

  init()
}

export default setupTimeline
